'use client';

import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '@/lib/http/client';
import { activityBus } from '@/lib/http/activity-bus';
import type { AuthPool } from '@/lib/auth-pool';
import { SESSION_POOL_HEADER } from './constants';
import { enforceIdle } from './enforce';
import { idleClock } from './idle-clock';
import { getPolicy } from './policy';
import {
    DISPLACED_LOGIN_HREF,
    TAB_SYNC_CHANNEL,
    activityApplies,
    activityPool,
    getTabId,
    isTabSyncMessage,
    markTabDisplaced,
    readTabClaim,
    shouldDisplaceTab,
    signsOutForDisplacement,
} from './tab-sync';
import type { IdleDecision, SessionPolicy } from './types';

type ExtendStatus = 'idle' | 'pending' | 'error';

/**
 * Minimum gap between server heartbeats. The server-side idle clock (the
 * `tai-last-activity` cookie) otherwise only advances on user-initiated API calls,
 * so a present-but-API-idle user (reading, typing, watching media) keeps the
 * CLIENT clock fresh — suppressing the warning banner — while the SERVER clock
 * goes stale and silently 401s the next request. Heartbeating on real DOM activity
 * keeps the two clocks in sync. 60s is well under the 5-minute warning window.
 */
const HEARTBEAT_THROTTLE_MS = 60_000;
const HEARTBEAT_ACTIVITY_MAX_LAG_MS = 2_000;

interface SessionContextValue {
    policy: SessionPolicy;
    lastActivityAt: number;
    decision: IdleDecision;
    extendSession: () => Promise<void>;
    extendPending: boolean;
    extendError: boolean;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children, initialActivityAt, pool = 'customer' }: {
    children: React.ReactNode;
    initialActivityAt?: number;
    pool?: AuthPool;
}) {
    // `'use client'` still means server-rendered for the initial HTML, and on the server
    // `idleClock` is one shared singleton per Node process rather than one per user. Every
    // read and write of it below is therefore browser-gated, and the SSR pass derives its
    // decision from this request's own props instead. See idle-clock.ts and
    // SessionProvider.ssr.test.tsx.
    const isBrowser = typeof window !== 'undefined';

    // Read the policy directly on the server: idleClock.getPolicy() memoises into module
    // scope, which is harmless while the policy is a constant but would become another
    // cross-request leak the moment it is derived per-request (the planned
    // compliance_profile lookup in policy.ts).
    const policy = isBrowser ? idleClock.getPolicy() : getPolicy();
    // `tick` exists only to re-render; the clock itself is the source of truth.
    const [, setTick] = useState(0);
    const [extendStatus, setExtendStatus] = useState<ExtendStatus>('idle');
    const lastHeartbeatRef = useRef(0);
    // The route pool is only the fallback. Analytics, admin, and the viewers
    // adopt the employee pool when that is the session they loaded; heartbeats
    // and activity must follow that, not the URL.
    const routePoolRef = useRef(pool);
    routePoolRef.current = pool;

    // Adopt the server's stamp before anything else can advance the clock. Done in
    // the first render rather than an effect so the very first paint already
    // reflects a session that expired while the tab was closed, instead of briefly
    // flashing case data. Guarded by a ref: on the first render no subscriber is
    // registered yet (subscribe happens in an effect below), so the notify inside
    // observeServerActivity reaches nobody and cannot become a render-phase update.
    const seededRef = useRef(false);
    if (isBrowser && !seededRef.current && typeof initialActivityAt === 'number') {
        seededRef.current = true;
        idleClock.observeServerActivity(initialActivityAt);
    }

    useEffect(() => idleClock.subscribe(() => setTick((n) => n + 1)), []);

    useEffect(() => {
        const channel = new BroadcastChannel(TAB_SYNC_CHANNEL);
        const poolNow = () => activityPool(routePoolRef.current);

        // Activity moves the idle clock for the pool this tab is using.
        // A tenant or user change marks that pool displaced. This tab signs out
        // when that pool is the one it is using now. The other pool stays
        // recorded so a later page does not adopt the replaced cookie.
        channel.onmessage = (event: MessageEvent) => {
            const data = event.data;
            if (!isTabSyncMessage(data)) return;
            const localPool = poolNow();

            if (data.type === 'activity') {
                if (localPool && activityApplies(data.pool, localPool)) {
                    idleClock.adoptRemoteActivity(data.ts);
                }
                return;
            }

            if (shouldDisplaceTab({
                messageTabId: data.tabId,
                localTabId: getTabId(),
                messagePool: data.pool,
                localPool: data.pool,
                rememberedClaim: readTabClaim(data.pool),
                messageUserId: data.userId,
                messageTenantId: data.tenantId,
            })) {
                markTabDisplaced(data.pool);
                if (signsOutForDisplacement(localPool, data.pool)) {
                    window.location.replace(DISPLACED_LOGIN_HREF);
                }
            }
        };

        // On local activity: update the clock and tell every other open tab.
        const unsub = activityBus.subscribe((ts) => {
            // markActivity returns false when the deadline had already passed, which
            // is the TR-3393 wake-up case: the mousemove that woke the machine must
            // not revive the session, nor be broadcast to other tabs as presence, nor
            // heartbeat the server cookie back to life.
            if (!idleClock.markActivity(ts)) {
                return;
            }

            const localPool = poolNow();
            if (!localPool) return;

            channel.postMessage({ type: 'activity', pool: localPool, ts });

            // Sync the server-side idle cookie with real DOM presence (throttled).
            // Plain fetch — not tpFetch — so a heartbeat that races an already-expired
            // server session doesn't itself trigger the sign-out redirect; the next
            // genuine user request handles a real expiry. activityBus already
            // coalesces DOM events to ~10s, so this fires at most once per minute.
            // Trailing coalesced notifications keep the local clock accurate, but
            // should not stamp the server cookie with the timer-fire time after
            // the user has already gone idle.
            const now = Date.now();
            if (
                now - ts <= HEARTBEAT_ACTIVITY_MAX_LAG_MS &&
                now - lastHeartbeatRef.current >= HEARTBEAT_THROTTLE_MS
            ) {
                lastHeartbeatRef.current = now;
                void fetch('/api/session/extend', {
                    method: 'POST',
                    credentials: 'include',
                    keepalive: true,
                    headers: { [SESSION_POOL_HEADER]: localPool },
                }).catch(() => {
                    // Transient — next activity tick retries.
                });
            }
        });

        return () => {
            unsub();
            channel.close();
        };
    }, []);

    useEffect(() => {
        // Re-evaluating on an interval alone is not enough: timers are suspended
        // while the machine sleeps, so a 103-minute absence produced no tick until
        // after the wake-up input had already been processed. Evaluate on wake
        // signals too, so the lock appears immediately on return rather than up to
        // one tick later. (Correctness no longer depends on winning that race — the
        // clock latches — but the user should not see the app at all post-deadline.)
        const evaluate = () => {
            idleClock.decisionAt(Date.now());
            setTick((n) => n + 1);
        };

        const id = setInterval(evaluate, 10_000);
        const onVisibility = () => {
            if (document.visibilityState === 'visible') evaluate();
        };

        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('focus', evaluate);
        window.addEventListener('pageshow', evaluate);

        return () => {
            clearInterval(id);
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('focus', evaluate);
            window.removeEventListener('pageshow', evaluate);
        };
    }, []);

    // On the server, decide from this request's own seed. Rendering the lock during SSR is
    // deliberate — it is what stops a walked-away session painting case data before
    // hydration — so the SSR pass has to reach the same verdict the client's first render
    // will, just without going through shared state.
    const lastActivityAt = isBrowser
        ? idleClock.getLastActivityAt()
        : (initialActivityAt ?? Date.now());
    const decision = isBrowser
        ? idleClock.decisionAt(Date.now())
        : enforceIdle({ lastActivityMs: lastActivityAt, policy, now: Date.now() });

    // Auto-clear a transient extend error once the session is healthy again (another
    // tab refreshed the cookie, or the user performed an action that reset the clock).
    useEffect(() => {
        if (decision.kind === 'ok' && extendStatus === 'error') {
            setExtendStatus('idle');
        }
    }, [decision, extendStatus]);

    const value = useMemo<SessionContextValue>(() => ({
        policy,
        lastActivityAt,
        decision,
        extendPending: extendStatus === 'pending',
        extendError: extendStatus === 'error',
        extendSession: async () => {
            // Set pending first — this single atomic update suppresses the expired
            // redirect guard in IdleTimeoutManager for the duration of the round-trip.
            setExtendStatus('pending');
            try {
                await api.post('/api/session/extend', undefined, {
                    signal: AbortSignal.timeout(10_000),
                    headers: { [SESSION_POOL_HEADER]: activityPool(pool) ?? pool },
                });
                // 2xx means the server re-checked the same idle limit and agreed the
                // session is alive, so this is the one path allowed to clear the
                // client's expired latch.
                idleClock.extendConfirmed(Date.now());
                setExtendStatus('idle');
            } catch {
                // tpFetch already redirects to /api/sign-out on idle-timeout 401.
                // All other rejections (network blip, 5xx, AbortError) are transient.
                // Expose via extendError so the user can retry without being signed out.
                // This single state update atomically clears pending and sets error,
                // so the redirect guard sees both changes in the same render.
                setExtendStatus('error');
            }
        },
    }), [policy, lastActivityAt, decision, extendStatus, pool]);

    return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
    const ctx = useContext(SessionContext);
    if (!ctx) throw new Error('useSession must be used inside <SessionProvider>');
    return ctx;
}
